<template>
  <div :id="`order-status-select-${index}`" class="select-box">
    <div
      :id="`order-status-select-container${index}`"
      class="select-box__selected"
      :class="{ 'select-box__selected--disabled': disabled }"
      @click="toggleDropdown"
    >
      <div class="select-box__field" :class="`select-box__field--${selected}`">
        {{ t(selected) }}
      </div>
      <template v-if="isShowArrow">
        <SvgLoader name="arrowDown" class="select-box__arrow" />
      </template>
      <template v-else>
        <!-- 矢印を見せない場合 -->
        <div class="select-box__arrow" />
      </template>
    </div>
    <Teleport v-if="isOpen" to="body">
      <div
        class="select-box__dropdown"
        :style="{
          top: `${yLength}px`,
          left: `${xLength}px`,
        }"
      >
        <div
          v-for="option in OrderStatus"
          v-show="!disabledOptions.includes(option)"
          :key="option"
          class="select-box__option"
          :class="{ 'select-box__option--disabled': !enableOptions.includes(option) }"
          @click="selectOption(option)"
        >
          {{ t(option) }}
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, computed, defineProps, defineEmits, onMounted, onUnmounted } from 'vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { openConfirmModal } from '@/corePagesModule/components/modal/modalActions';
import { OrderStatusService } from '../domains/services/orderStatusService';
import { i18n } from '@/i18n';
import { OrderStatus } from '@/coreDomainsModule/entities/arrival';

const t = i18n.t;

const props = withDefaults(
  defineProps<{
    index: number;
    modelValue: OrderStatus;
    disabled?: boolean;
    isShowArrow?: boolean;
  }>(),
  {
    disabled: false,
    isShowArrow: true,
  }
);

const selected = ref<OrderStatus>(props.modelValue);
const isOpen = ref(false);

const result = ref<DOMRect>();
const xLength = ref(0);
const yLength = ref(0);

const enableOptions = computed((): string[] => {
  return OrderStatusService.getEnabledNextOrderStatus(selected.value);
});

const disabledOptions: string[] = [OrderStatus.NoRecommendation, OrderStatus.Delivered, OrderStatus.DisplayEnded];

/**
 * ユーザーが選択を変更するときに確認モーダルを表示します。
 * @param value - 新しい選択値
 * @param beforeValue - 変更前の選択値
 * @returns モーダルの結果をPromiseで返します。
 */
const checkModal = async (value: OrderStatus, beforeValue: OrderStatus): Promise<boolean> => {
  if (!OrderStatusService.isUserConfirmationRequiredForChange(value, beforeValue)) return true;
  // モーダル表示
  const result = await openConfirmModal(
    'question',
    t('発注ステータス更新確認テキスト', { beforeStatus: t(beforeValue), afterStatus: t(value) }),
    t('変更する'),
    t('キャンセル')
  );
  return result !== undefined && result.isConfirmed;
};

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
}>();

watch(
  () => props.modelValue,
  () => {
    selected.value = props.modelValue;
  }
);

watch(selected, (newValue, oldValue) => {
  checkModal(newValue as OrderStatus, oldValue as OrderStatus).then((result) => {
    if (result) {
      emit('update:modelValue', newValue);
    } else {
      selected.value = oldValue;
    }
  });
});

/**
 * ドロップダウンの表示状態を切り替えます。
 */
const toggleDropdown = () => {
  if (!props.disabled) {
    isOpen.value = !isOpen.value;
  }
  if (isOpen.value) {
    const element = document.getElementById(`order-status-select-container${props.index}`);
    if (!element) return;
    result.value = element.getBoundingClientRect();
    xLength.value = result.value.left;
    yLength.value = result.value.bottom;
  }
};

/**
 * オプションを選択し、親コンポーネントに選択値を通知します。
 * @param option - 選択されたオプション
 */
const selectOption = (option: OrderStatus) => {
  if (enableOptions.value.includes(option)) {
    selected.value = option;
    emit('update:modelValue', option);
    isOpen.value = false;
  }
};

/**
 * ドロップダウンを閉じます。
 * @param event - クリックイベント
 */
const closeDropdown = (event: MouseEvent) => {
  if (!(event.target as Element).closest(`#order-status-select-${props.index}`)) {
    isOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', closeDropdown);
});

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown);
});
</script>

<style scoped lang="scss">
.select-box {
  justify-items: center;
  position: relative;
  font-weight: bold;

  &__field {
    width: 80px;
    height: 20px;

    font-family: Inter;
    font-size: $fontSizeSS;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: center;
    background-color: #757575;
    border-radius: 90px;

    &--Recommended {
      background-color: $optionalOrange;
    }
    &--ApprovalRequested {
      background-color: $optionalPink;
    }
    &--ApprovedAwaitingDeliveryDate {
      background-color: $optionalViolet;
    }
  }

  &__selected {
    width: 100%;
    color: white;
    cursor: pointer;
    display: grid;
    grid-template-columns: 1fr 24px;
    text-align: center;
    align-items: center;

    &--disabled {
      background-color: #ccc;
      cursor: not-allowed;
    }
  }

  &__arrow {
    display: grid;
    align-content: center;
    justify-content: center;
    height: 24px;
    width: 24px;
  }

  &__dropdown {
    position: absolute;
    width: 104px;
    background-color: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    z-index: 1;
    height: 120px;
    overflow-y: auto;
  }

  &__option {
    padding: 10px;
    cursor: pointer;

    &:hover,
    &:focus {
      background-color: #f0f0f0;
    }

    &--disabled {
      color: #ccc;
      cursor: not-allowed;
    }
  }
}
</style>
